import React from 'react';
import { clsx } from 'clsx';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { CashPoolProduct } from '@/types/cash-pool.types';
import { MtmPage, MtmLeg } from '@/features/trades/pages/MtmPage';
import { FolderTabs } from '../components/FolderTabs';
import { useRovingTabs } from '../components/use-roving-tabs';

/**
 * Collateral Manager (LR-580 / D1, restructured by LR-587 to the product's
 * 12.08 spec).
 *
 * Level 1 is the agreement — GMRA | GMSLA — and level 2 the collateral leg:
 * GMRA → Cash Pool (the repo margin call view, the only thing that moves a GMRA
 * pool), GMSLA → Cash Pool Fee | Cash Rebate (the marking workspace, split by
 * the trade's own rate entry mode). Non-Cash (both products) and
 * Uncollateralized are product-deferred (answers #1/#2, 12.08: on hold /
 * "remove from the UI at this stage") and deliberately carry no tab — this
 * strip is where they land when they arrive.
 *
 * The pool register (list + create/edit) moved to Registers, next to
 * counterparties and custodians (product answer 10.08: "В Registers нужно будет
 * раздел Cash Pools"): this section is the working view of the collateral, not
 * the reference data behind it.
 *
 * Product, leg and the MTM counterparty scope live in the URL (LR-587) — the
 * view is shareable and survives F5, which the local state D1 shipped with was
 * not.
 */

const PRODUCT_TABS = [
  { key: 'gmra', label: 'GMRA' },
  { key: 'gmsla', label: 'GMSLA' },
] as const;

type CollateralLeg = 'cash' | 'fee' | 'rebate';

const LEG_TABS: Record<
  CashPoolProduct,
  readonly { key: CollateralLeg; label: string }[]
> = {
  gmra: [{ key: 'cash', label: 'Cash Pool' }],
  gmsla: [
    { key: 'fee', label: 'Cash Pool Fee' },
    { key: 'rebate', label: 'Cash Rebate' },
  ],
};

// Each product opens on its first leg; also the landing leg when the product
// switches (a leg of the other product would be meaningless here).
const DEFAULT_LEG: Record<CashPoolProduct, CollateralLeg> = {
  gmra: 'cash',
  gmsla: 'fee',
};

const TAB_BUTTON_CLASSES =
  'px-3 py-1.5 text-lg font-semibold motion-safe:transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export const CollateralManagerPage: React.FC = () => {
  const { product, leg, cp } = useSearch({ from: '/collateral' });
  const navigate = useNavigate();

  // `replace`, and the counterparty scope is preserved: switching product or leg
  // is not a new place in the history, and the trader stays on the CP they were
  // on (both workspaces tab by the same counterparty list).
  const setProduct = (next: CashPoolProduct): void => {
    navigate({
      to: '/collateral',
      search: { product: next, leg: DEFAULT_LEG[next], cp },
      replace: true,
    });
  };

  const setLeg = (next: CollateralLeg): void => {
    navigate({ to: '/collateral', search: { product, leg: next, cp }, replace: true });
  };

  // Same keyboard contract the leg strip below gets from FolderTabs — this level
  // is styled by hand (underline, not folder), which is exactly how a tablist
  // ends up with `role`/`aria-selected` and no way to move between its tabs.
  const { refs: tabRefs, onKeyDown } = useRovingTabs(
    PRODUCT_TABS.map((tab) => tab.key),
    setProduct,
  );

  return (
    <div className="relative flex h-[calc(100vh-0px)] flex-col">
      <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
        <CardHeader className="flex flex-col gap-2 pb-0 pt-2">
          <div
            className="flex items-center gap-1"
            role="tablist"
            aria-label="Collateral Manager"
          >
            {PRODUCT_TABS.map((tab, index) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`collateral-tab-${tab.key}`}
                aria-controls="collateral-panel"
                aria-selected={product === tab.key}
                tabIndex={product === tab.key ? 0 : -1}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                onKeyDown={(event) => onKeyDown(event, index)}
                onClick={() => setProduct(tab.key)}
                className={clsx(
                  TAB_BUTTON_CLASSES,
                  product === tab.key
                    ? 'border-b-2 border-blue-600 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <FolderTabs
            tabs={LEG_TABS[product]}
            active={leg}
            onChange={setLeg}
            ariaLabel="Collateral leg"
            idPrefix="collateral-leg-tab"
            panelId="collateral-leg-panel"
          />
        </CardHeader>

        <CardContent
          role="tabpanel"
          id="collateral-panel"
          aria-labelledby={`collateral-tab-${product}`}
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-0"
        >
          {/* The leg strip's own panel, nested inside the product one: each
              tablist owns exactly one panel, so neither group is left with an
              ambiguous owner and a screen reader is told what the leg tabs
              switch. */}
          <div
            role="tabpanel"
            id="collateral-leg-panel"
            aria-labelledby={`collateral-leg-tab-${leg}`}
            className="flex min-h-0 flex-1 flex-col"
          >
            {product === 'gmra' ? (
              <MtmPage scope="repo" />
            ) : (
              // key: the two legs are disjoint books sharing the global mtm-store;
              // remounting on a leg switch re-runs the per-book reset so a
              // selection made on one leg cannot leak into the other.
              <MtmPage key={leg} scope="stock_lb" leg={leg as MtmLeg} />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
